import Image from 'next/image';
import { cn } from 'utils/styles';
import { CardStyleIndicator } from 'components/shared/Card/CardStyle';
import { BaseCard, BaseCardBox, BaseCardContent } from 'components/shared/Card/BaseCard';
import HyperlinkedMedia from 'components/shared/HyperlinkedMedia/HyperlinkedMedia';
import RichTextCopy from 'components/shared/RichTextCopy/RichTextCopy';
import FiveStars from 'components/shared/Icons/FiveStar';
import { CardsItemImageLayout } from 'types/constants';
import { CardIconItem } from '../BaseCard/types';
import { liveUpdateAttrs } from 'libs/contentful/utils';

interface CardIconItemProps extends Omit<CardIconItem, 'name'> {
  index: number;
  total: number;
  cardStyle: string;
}

function CardsIconItem({
  sys,
  index,
  total,
  cardIcon,
  cardHeadlineRte,
  cardCopy,
  color,
  cardStyle,
  showFiveStarEyebrow,
  jumpToCta,
  textEyebrowRte,
  hyperlinkedImagesCollection,
  imageLayout,
  testId,
}: CardIconItemProps) {
  const isNoneCardStyle = cardStyle === 'None';
  const displayFiveStarEyebrow = showFiveStarEyebrow && !textEyebrowRte;
  const flexDirection = imageLayout === CardsItemImageLayout.Vertical ? 'flex-col' : 'flex-row flex-wrap';

  const testFixture = testId ?? 'card-icon-item';

  return (
    <BaseCard color={color.name} className="min-h-[32rem]" data-testid={`${testFixture}-base`}>
      <BaseCardBox
        className={cn({ 'flex h-full items-center justify-center': isNoneCardStyle })}
        data-testid={`${testFixture}-box`}>
        <CardStyleIndicator
          color={color.name}
          cardStyle={cardStyle}
          index={index}
          total={total}
          data-testid={testFixture}
        />
        <div className="empty:pt-6" data-testid={`${testFixture}-media-wrapper`}>
          {(textEyebrowRte || displayFiveStarEyebrow) && (
            <div className="mb-8" data-testid={`${testFixture}-eyebrow-wrapper`}>
              {textEyebrowRte && (
                <div className="font-sans font-medium text-eyebrow" {...liveUpdateAttrs(sys?.id, 'textEyebrow')}>
                  <RichTextCopy copy={textEyebrowRte} data-testid={`${testFixture}-eyebrow`} />
                </div>
              )}
              {displayFiveStarEyebrow && <FiveStars data-testid={`${testFixture}-five-stars`} />}
            </div>
          )}
          {cardIcon?.url ? (
            <Image
              src={cardIcon.url}
              alt={cardIcon.description ?? ''}
              height={72}
              width={72}
              priority
              className={cn('object-contain object-left mt-6 mb-8 w-[100%] h-[72px]')}
              {...liveUpdateAttrs(sys?.id, 'cardIcon')}
            />
          ) : null}
        </div>
        <BaseCardContent
          sys={sys}
          cardHeadlineRte={cardHeadlineRte}
          cardCopy={cardCopy}
          jumpToCta={jumpToCta}
          testId={testFixture}
        />
        {hyperlinkedImagesCollection?.items != null && hyperlinkedImagesCollection.items.length > 0 ? (
          <div
            className={`flex ${flexDirection} gap-2 mt-6`}
            {...liveUpdateAttrs(sys?.id, 'hyperlinkedImages')}
            data-testid={testFixture}>
            {hyperlinkedImagesCollection.items.map(media => {
              return (
                <HyperlinkedMedia
                  link={media.link}
                  image={media.image}
                  key={Buffer.from(media.image.url).toString('hex')}
                  targetHeight={imageLayout === CardsItemImageLayout.Horizontal ? 40 : undefined}
                />
              );
            })}
          </div>
        ) : null}
      </BaseCardBox>
    </BaseCard>
  );
}

export default CardsIconItem;
